import {
    Component,
    ojs,
    app,
    state,
    State,
    v,
    payload,
} from "modular-openscriptjs";
import {
    either,
    monetary,
    minimizeString,
    isQualifiedUrl,
    getProductAvailabilityWindow,
} from "../../helpers";
import { dc, uc } from "../../declarations";
import { $e } from "../../ojs.events";

const h = app("h");
const broker = app("broker");
const userCurrency = dc.userCurrency;

export default class SearchResultProductView extends Component {
    render(product, ...args) {
        const p = product instanceof State ? product.value : product;
        if (!p?.id) return h.empty();

        const hasImage = p.images && p.images.length > 0;
        let imagePath = hasImage ? p.images[0].path : p.thumbnail_path;
        const finalHasImage = !!imagePath;

        const ownerName = p.company?.name || p.owner?.name || "Carata Seller";
        const price = p.final_price || p.selling_price || p.price;
        const hasDiscount =
            p.discounted_price != null && p.discounted_price < price;
        const discountPercent =
            hasDiscount && price > 0
                ? Math.round((1 - p.discounted_price / price) * 100)
                : 0;
        // Product-specific time-of-day window (e.g. a dinner-only dish) -
        // separate from the shop's own opening hours (is_available).
        const timeAvailability = getProductAvailabilityWindow(p);

        return h.div(
            {
                class: "search-result-card overflow-hidden rounded-4 bg-white mb-4 transition-all hover-shadow-lg border-0",
                style: "display: grid; grid-template-columns: 120px 1fr; min-height: 120px; box-shadow: 0 4px 20px rgba(0,0,0,0.05);",
            },

            // Image/Fallback Section
            h.div(
                {
                    class: "position-relative overflow-hidden rounded-4",
                    style: "background: #f8f9fa;",
                },
                either(
                    finalHasImage,
                    () =>
                        h.img({
                            src: imagePath,
                            class: "w-100 h-100 object-fit-cover",
                            alt: p.name,
                            loading: "lazy",
                            style: "min-height: 100px",
                        }),
                    () =>
                        h.div(
                            {
                                class: "w-100 d-flex flex-column align-items-center justify-content-center p-3 text-center",
                                style: "height: 100px; background: linear-gradient(135deg, #fdfcfb 0%, #e2d1c3 100%);",
                            },
                            h.i({
                                class: "bi bi-box2 text-muted opacity-50 fs-2 mb-1",
                            }),
                            h.span(
                                {
                                    class: "fw-bold small text-muted opacity-75",
                                    style: "font-size: 0.65rem;",
                                },
                                "NO IMAGE",
                            ),
                        ),
                ),
                // Red clock badge - product is outside its available_from/
                // available_until window right now.
                either(!timeAvailability.available, () =>
                    h.div(
                        {
                            class: "position-absolute top-0 start-0 m-2 bg-danger text-white rounded-circle d-flex align-items-center justify-content-center shadow-sm",
                            style: "width: 26px; height: 26px;",
                            title:
                                timeAvailability.message ||
                                "Not available right now",
                        },
                        h.i({
                            class: "fas fa-clock",
                            style: "font-size: 0.75rem;",
                        }),
                    ),
                ),
                // Floating bottom-right add to cart
                h.div(
                    {
                        class: "position-absolute bottom-0 end-0 p-2",
                    },
                    h.button(
                        {
                            class: "btn btn-light rounded-circle shadow-sm d-flex align-items-center justify-content-center p-0 border",
                            style: "width: 32px; height: 32px; background: rgba(255, 255, 255, 0.95); backdrop-filter: blur(4px);",
                        },
                        h.i({ class: "fas fa-plus text-dark fa-2x" }),
                    ),
                ),
            ),

            // Content Section
            h.div(
                {
                    class: "py-2 px-3 d-flex flex-column justify-content-between position-relative",
                },

                h.div(
                    h.div(
                        { class: "mb-1" },
                        h.h6(
                            {
                                class: "fw-bold text-dark mb-0 pe-2",
                                style: "font-size: 1rem; line-height: 1.3;",
                            },
                            p.name,
                        ),
                    ),
                    h.div(
                        { class: "d-flex align-items-center mb-2" },
                        h.div({
                            class: "rounded-circle me-2",
                            style:
                                "width: 18px; height: 18px; background: #eee; background-image: url('" +
                                (p.company?.logo || "") +
                                "'); background-size: cover;",
                        }),
                        h.span(
                            {
                                class: "text-secondary fw-medium",
                                style: "font-size: 0.75rem;",
                            },
                            ownerName,
                        ),
                    ),

                    // Description excerpt if no image
                    either(!finalHasImage, () =>
                        h.p(
                            {
                                class: "text-muted mb-0",
                                style: "font-size: 0.85rem; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;",
                            },
                            p.short_description || "No description available.",
                        ),
                    ),
                ),

                h.div(
                    {
                        class: "d-flex align-items-center justify-content-between mt-2 pt-1 flex-wrap gap-1",
                    },
                    h.div(
                        { class: "d-flex align-items-center gap-2 flex-wrap" },
                        either(
                            hasDiscount,
                            () => [
                                h.span(
                                    {
                                        class: "text-dark fw-bold",
                                        style: "font-size: 1.05rem;",
                                    },
                                    monetary(p.discounted_price, userCurrency),
                                ),
                                h.span(
                                    {
                                        class: "text-muted text-decoration-line-through",
                                        style: "font-size: 0.8rem;",
                                    },
                                    monetary(price, userCurrency),
                                ),
                            ],
                            () =>
                                h.span(
                                    {
                                        class: "text-dark",
                                        style: "font-size: 1.05rem;",
                                    },
                                    monetary(price, userCurrency),
                                ),
                        ),
                    ),
                    either(hasDiscount, () =>
                        h.span(
                            {
                                class: "badge bg-danger-subtle text-danger rounded-pill d-inline-flex align-items-center gap-1 fw-bold",
                                style: "font-size: 0.7rem;",
                            },
                            h.i({ class: "fas fa-tag" }),
                            `-${discountPercent}%`,
                        ),
                    ),
                ),
            ),
            ...args,
        );
    }
}

ojs(SearchResultProductView);
